import { Box, Button } from "@chakra-ui/react";
import { FC } from "react";

import { Event as ThreediEvent } from "@nens/threedi/openapi-client";

import downloadFromUrl from "../../utils/downloadFromUrl";
import { fetchWithError } from "../../utils/query";

type EventFileDetailsProps = {
  eventKeys: string[];
  events: ThreediEvent;
  prettifyTitle: (value: string) => string;
};

// ThreediEvent values are optional arrays (e.g. Array<Breach> | undefined).
// NonNullable removes `undefined` from the union before extracting element types —
// otherwise `undefined extends Array<infer U>` resolves to `never` and pollutes the result.
// ArrayElement<T> then extracts the item type from each array in the union distributively
// (T[number] doesn't work here because TypeScript can't index a union of arrays),
// giving us the union of all possible event items: `Breach | Substance | ...`
type ArrayElement<T> = T extends Array<infer U> ? U : never;

type DownloadFileButtonProps = {
  eventKey: string;
  prettifyTitle: (value: string) => string;
  event: ArrayElement<NonNullable<ThreediEvent[keyof ThreediEvent]>>;
};

const DownloadFileButton: FC<DownloadFileButtonProps> = ({ eventKey, prettifyTitle, event }) => {
  if (!event) return null;
  const onDownload = async () => {
    if (!("file" in event)) return;
    const { get_url } = await fetchWithError(event.file.related_object + "download/");
    if (get_url) downloadFromUrl(get_url, eventKey);
  };

  return (
    <Box my="5">
      <strong>{prettifyTitle(eventKey)}</strong>
      <Button ml="5" onClick={onDownload} variant="secondary">
        Download file
      </Button>
    </Box>
  );
};

const EventFileDetails = ({ eventKeys, events, prettifyTitle }: EventFileDetailsProps) => (
  <>
    {eventKeys.map((key: string) => {
      const value = events[key as keyof ThreediEvent];
      if (!value) return null;
      return [value]
        .flat()
        .map((event, index) => (
          <DownloadFileButton
            prettifyTitle={prettifyTitle}
            key={key + index}
            eventKey={key}
            event={event}
          />
        ));
    })}
  </>
);

export default EventFileDetails;
